<template>
  <mds-table row-numbers fixed-first-col>
    <mds-thead :nested-headers="1">
      <!-- Nested Header -->
      <template v-slot:mds-thead-group-1>
        <mds-th
          v-for="header in headers.slice(0, 5)"
          :key="header.fieldName"
          :style="header.style"
          colspan="1"
        ></mds-th>
        <mds-th v-if="openTradingCommission" style="width: 390px" colspan="3">
          {{ translate('tradingCommissions') }}
        </mds-th>
      </template>
      <!-- Normal Headers -->
      <mds-th v-for="header in headers" :key="header.fieldName" :style="header.style">
        {{ header.text }}
      </mds-th>
    </mds-thead>
    <mds-tbody>
      <mds-tr v-for="(row, index) in securityTableData" :key="index" :row-number="index + 1">
        <mds-td v-for="header in headers" :key="header.fieldName" :style="header.style">
          <template v-if="header.fieldName === 'securityName'">
            {{ row[header.fieldName] }}
          </template>
          <template v-else-if="header.fieldName === 'frontLoadFeeType'">
            <mds-select
              v-if="!isSecurityWithTradingCommission(row)"
              v-model="row[header.fieldName]"
              hidden-label
              label="Front Load"
              :options="securityFrontLoadFeeTypeOptions"
              @change="handleFrontLoadFeeChanged($event, row)"
            ></mds-select>
            <template v-else>
              {{ '&mdash;' }}
            </template>
          </template>
          <template v-else-if="header.fieldName === 'frontLoadAmount'">
            <mds-input
              v-if="!isSecurityWithTradingCommission(row) && isCustomFrontLoadFee(row)"
              size="small"
              hidden-label
              label="Amount"
              :value="header.transform(row[header.fieldName], row.frontLoadFeeType)"
              @change="handleFrontLoadAmountChanged($event, row)"
            >
            </mds-input>
            <template v-else>
              {{ '&mdash;' }}
            </template>
          </template>
          <template v-else-if="header.fieldName === 'redemptionCharge'">
            <mds-select
              v-if="!isSecurityWithTradingCommission(row)"
              :value="row[header.fieldName]"
              hidden-label
              label="Redemp/Deferred Charge"
              :options="securityRedemptionChargeOptions"
              @change="handleRedemptionChargeChanged($event, row)"
            ></mds-select>
            <template v-else>
              {{ '&mdash;' }}
            </template>
          </template>
          <template v-else-if="header.fieldName === 'feeSchedule'">
            <span :class="$style['fee-link']" @click="$emit('show-fee-schedule', row)">{{
              translate('view')
            }}</span>
          </template>
          <template v-if="header.fieldName === 'tcFeeType'">
            <mds-select
              v-if="isSecurityWithTradingCommission(row)"
              :value="row.tradingCommission.tradingCommissionType"
              hidden-label
              label="Which fee"
              :options="tradingCommissionTypeOptions"
              @change="handleTradingCommissionTypeChange($event, row)"
            ></mds-select>
            <template v-else>
              {{ '&mdash;' }}
            </template>
          </template>
          <template v-else-if="header.fieldName === 'tcAmountType'">
            <mds-select
              v-if="isSecurityWithTradingCommission(row)"
              :value="row.tradingCommission.amountType"
              hidden-label
              label="Trading Commission Amount Type"
              :options="tradingCommissionAmountTypeOptions"
              @change="handleTCTypeChange($event, row)"
            ></mds-select>
            <template v-else>
              {{ '&mdash;' }}
            </template>
          </template>
          <template v-else-if="header.fieldName === 'tcAmount'">
            <mds-input
              v-if="isSecurityWithTradingCommission(row)"
              :class="$style['trading-commission-input']"
              size="small"
              hidden-label
              label="Amount"
              :value="formatter.number(row.tradingCommission.tradingCommissionAmount, 'integer')"
              @change="handleTCAmountChange($event, row)"
            >
            </mds-input>
            <template v-else>
              {{ '&mdash;' }}
            </template>
          </template>
        </mds-td>
      </mds-tr>
    </mds-tbody>
  </mds-table>
</template>

<script>
import MwcVueHelperMixin, { MwcConfigMixin } from 'mwc-vue-component-helper';
import { MdsTable, MdsThead, MdsTh, MdsTbody, MdsTr, MdsTd } from '@mds/data-table';
import MdsInput from '@mds/input';
import MdsSelect from '@mds/select';
import cloneDeep from 'lodash.clonedeep';
import { parseNumber, isInvalidAmount, isInvalidPercentage } from '../../utils/utils';
import { TRADING_COMISSION_TYPES, FRONT_LOAD_FEE_TYPE_OPTIONS } from '../../utils/constants';
import {
  redemptionChargeOptions,
  securityFrontLoadFeeTypeOptions,
  tradingCommissionTypeOptions,
} from '../../utils/input-options';

const { PERCENT } = TRADING_COMISSION_TYPES;

export default {
  name: 'transaction-fee-table',
  components: {
    MdsTable,
    MdsThead,
    MdsTh,
    MdsTbody,
    MdsTr,
    MdsTd,
    MdsInput,
    MdsSelect,
  },
  mixins: [MwcVueHelperMixin, MwcConfigMixin],
  props: {
    currencySymbol: {
      type: String,
      default: '$',
    },
    securities: Array,
    openTradingCommission: Boolean,
    propSecurityTableData: Array,
  },
  data() {
    return {
      defaultConfig: {
        settings: {
          languageId: this.getComponentConfig().settings.languageId || 'en-US',
        },
      },
      securityTableData: [],
    };
  },
  computed: {
    headers() {
      const headers = [
        {
          text: this.translate('name'),
          fieldName: 'securityName',
          style: {
            width: '350px',
          },
        },
        {
          text: this.translate('frontLoad'),
          fieldName: 'frontLoadFeeType',
          style: {
            width: '150px',
          },
        },
        {
          text: this.translate('customFrontLoadAmount'),
          fieldName: 'frontLoadAmount',
          transform: (value, frontLoadFeeType) => {
            const isCustomAmount = frontLoadFeeType === FRONT_LOAD_FEE_TYPE_OPTIONS.CUSTOM_AMOUNT;
            const val = value || 0;
            return isCustomAmount
              ? this.formatter.number(val)
              : this.formatter.number(val, 'longDecimal');
          },
          style: {
            width: '180px',
          },
        },
        {
          text: this.translate('redempDeferredChange'),
          fieldName: 'redemptionCharge',
          style: {
            width: '180px',
          },
        },
        {
          text: this.translate('feesHoldingSchedule'),
          fieldName: 'feeSchedule',
          style: {
            width: '160px',
          },
        },
        {
          text: this.translate('whichFee'),
          fieldName: 'tcFeeType',
          style: {
            width: '180px',
          },
        },
        {
          text: this.translate('Type'),
          fieldName: 'tcAmountType',
          style: {
            width: '120px',
          },
        },
        {
          text: this.translate('amount'),
          fieldName: 'tcAmount',
          style: {
            width: '150px',
          },
        },
      ];
      if (!this.openTradingCommission) {
        headers.splice(5, 3);
      }
      return headers;
    },
    securityFrontLoadFeeTypeOptions() {
      return securityFrontLoadFeeTypeOptions
        .filter(i => {
          return this.showLoiAsFrontLoad || i.value !== FRONT_LOAD_FEE_TYPE_OPTIONS.LOI;
        })
        .map(({ labelKey, value }) => {
          const isCustomAmount = value === FRONT_LOAD_FEE_TYPE_OPTIONS.CUSTOM_AMOUNT;
          const text = isCustomAmount
            ? `${this.translate(labelKey)} ${this.currencySymbol}`
            : this.translate(labelKey);
          return {
            text,
            value,
          };
        });
    },
    securityRedemptionChargeOptions() {
      return this.translateComponentOptions(redemptionChargeOptions);
    },
    tradingCommissionTypeOptions() {
      return this.translateComponentOptions(tradingCommissionTypeOptions);
    },
    tradingCommissionAmountTypeOptions() {
      return [
        { text: `${this.currencySymbol}`, value: 0 },
        { text: `%`, value: 1 },
      ];
    },
  },
  watch: {
    propSecurityTableData: {
      handler() {
        this.securityTableData = cloneDeep(this.propSecurityTableData);
      },
      deep: true,
    },
  },
  created() {
    this.securityTableData = cloneDeep(this.propSecurityTableData);
  },
  methods: {
    emitSecuritiesDataChanged() {
      this.$emit('security-transaction-fee-changed', cloneDeep(this.securityTableData));
    },
    isSecurityWithTradingCommission(sec) {
      return ['ST', 'FC', 'FE'].includes(sec.securityType);
    },
    handleFrontLoadFeeChanged(frontLoadFeeType, row) {
      const { securityId } = row;
      const isCustom =
        frontLoadFeeType === FRONT_LOAD_FEE_TYPE_OPTIONS.CUSTOM_PERCENT ||
        frontLoadFeeType === FRONT_LOAD_FEE_TYPE_OPTIONS.CUSTOM_AMOUNT;
      if (isCustom) {
        this.securityTableData = this.securityTableData.map(security => {
          if (security.securityId === securityId) {
            return {
              ...security,
              frontLoadAmount: 0,
            };
          }
          return security;
        });
      }
      this.emitSecuritiesDataChanged();
    },
    isCustomFrontLoadFee({ frontLoadFeeType }) {
      return (
        frontLoadFeeType === FRONT_LOAD_FEE_TYPE_OPTIONS.CUSTOM_AMOUNT ||
        frontLoadFeeType === FRONT_LOAD_FEE_TYPE_OPTIONS.CUSTOM_PERCENT
      );
    },
    handleFrontLoadAmountChanged(e, row) {
      const { securityId, frontLoadFeeType, frontLoadAmount } = row;
      const isCustomAmount = frontLoadFeeType === FRONT_LOAD_FEE_TYPE_OPTIONS.CUSTOM_AMOUNT;
      const decimalDigits = isCustomAmount ? 2 : 4;
      const inputNum = parseNumber(e.target.value, decimalDigits);
      const isInvalid = isCustomAmount ? isInvalidAmount : isInvalidPercentage;
      if (isInvalid(inputNum)) {
        const rawVal = isCustomAmount
          ? this.formatter.number(frontLoadAmount)
          : this.formatter.number(frontLoadAmount, 'longDecimal');
        e.target.value = rawVal;
      } else {
        this.securityTableData = this.securityTableData.map(security => {
          if (security.securityId === securityId) {
            return {
              ...security,
              frontLoadAmount: inputNum,
            };
          }
          return security;
        });
      }
      this.emitSecuritiesDataChanged();
    },
    translateComponentOptions(options) {
      return options.map(({ labelKey, value }) => {
        return {
          text: this.translate(labelKey),
          value,
        };
      });
    },
    handleRedemptionChargeChanged(value, row) {
      const { securityId } = row;
      const redemptionCharge = parseInt(value, 10);
      this.securityTableData = this.securityTableData.map(security => {
        if (security.securityId === securityId) {
          return {
            ...security,
            redemptionCharge,
          };
        }
        return security;
      });
      this.emitSecuritiesDataChanged();
    },
    handleTradingCommissionTypeChange(val, row) {
      row.tradingCommission.tradingCommissionType = Number(val);
      this.emitSecuritiesDataChanged();
    },
    handleTCTypeChange(amountType, row) {
      row.tradingCommission.amountType = Number(amountType);
      row.tradingCommission.tradingCommissionAmount = 0;
      this.emitSecuritiesDataChanged();
    },
    handleTCAmountChange(e, row) {
      const amount = parseNumber(e.target.value);
      const secTC = row.tradingCommission;
      const invalidPercent = secTC.amountType === PERCENT && isInvalidPercentage(amount);
      const numberUnchanged = amount === secTC.tradingCommissionAmount;
      if (isInvalidAmount(amount) || invalidPercent || numberUnchanged) {
        e.target.value = this.formatter.number(secTC.tradingCommissionAmount, 'integer');
      } else {
        secTC.tradingCommissionAmount = amount;
      }
      this.emitSecuritiesDataChanged();
    },
  },
};
</script>

<style lang="scss" module>
@import '~@mds/link';

.fee-link {
  @include mds-link($underline: false);
  cursor: pointer;
  display: inline-block;
  margin-top: $mds-space-1-x;
}
.trading-commission-input {
  margin-right: $mds-space-3-x;
}
</style>
